import React, { useCallback, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/src/components/ui/button";

import OfferPlanDrawer from "./drawer/OfferPlanDrawer";
import { getNewOfferPlan } from "./offerPlanAdapter";
import {
  getAvailableOfferPlanProvider,
  getAvailablePlatforms,
} from "./offersFormParams/offersFormParams";
import { offerPlanColumns, offerPlanRow } from "./tableParams/offerPlanColumns";
import OfferAPI from "../../api/OfferAPI";
import ProjectAPI from "../../api/ProjectAPI";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import { getSelectedCountries } from "../../utils/data/Countries";
import DataTable from "../dataTable/DataTable";

import type { JSX } from "react";

import type { FlatOfferPlan } from "./offerPlanAdapter";
import type { OfferPlanRow } from "./tableParams/offerPlanColumns.types";
import type { OfferPlanTypeValue } from "../../models/OfferPlan.types";
import type { AppState } from "../../reducers";
import type { OfferPlanType } from "../../types";

interface OfferPlanTableProps {
  plans: OfferPlanType[];
  planType: OfferPlanTypeValue;
  onPlanChange: (updatedPlan: FlatOfferPlan) => void;
  onPlanAdd: (newPlan: FlatOfferPlan) => void;
  onPlanDelete: (planId: string) => void;
}

function OfferPlanTable({
  plans,
  planType,
  onPlanAdd,
  onPlanDelete,
  onPlanChange,
}: OfferPlanTableProps): JSX.Element {
  const { projectSlug, id: offerId } = useParams<{
    projectSlug: string;
    id: string;
  }>();
  const { t } = useTranslation("editor-offers");
  const project = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );
  const countries = useSelector(
    (state: AppState) => state.fetchReducer.countries,
  );
  const copyToClipboard = useCopyToClipboard();

  const [editingPlan, setEditingPlan] = useState<OfferPlanType | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const getPlatformsQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getPlatforms,
    params: [{ projectSlug }],
  });

  const { data: platformData } = useQuery(getPlatformsQuery);

  const availablePlatform = getAvailablePlatforms(planType, platformData);

  // Memoized so its reference is stable across renders: the drawer keys its
  // zod-resolver memo and a derivation effect on this list, which would
  // otherwise be rebuilt on every render of this table.
  const availableProviderPlanList = useMemo(
    () => getAvailableOfferPlanProvider(platformData?.providers ?? []),
    [platformData?.providers],
  );

  const retailerList =
    platformData?.retailers?.map((r) => ({ value: r.uuid, label: r.name })) ??
    [];

  const handleAddPlanClick = (): void => {
    setEditingPlan(
      getNewOfferPlan({
        offerPlanType: planType,
        providerPlans: availableProviderPlanList,
      }),
    );
    setIsDrawerOpen(true);
  };

  const handleEditClick = useCallback(
    (uuid: string): void => {
      const planToEdit = plans.find((currentPlan) => currentPlan.uuid === uuid);

      if (planToEdit) {
        setEditingPlan(planToEdit);
        setIsDrawerOpen(true);
      }
    },
    [plans],
  );

  const handleDrawerSaved = (savedPlan: FlatOfferPlan): void => {
    if (savedPlan.isNew) {
      // Freshly created: it now carries the server-assigned uuid (reconciled in
      // the drawer), so add it as non-new — re-editing updates it in place
      // instead of creating a duplicate.
      onPlanAdd({ ...savedPlan, isNew: false });
    } else {
      onPlanChange(savedPlan);
    }
  };

  const handleDelete = useCallback(
    async (row: OfferPlanRow): Promise<void> => {
      const planToDelete = plans.find(
        (currentPlan) => currentPlan.uuid === row.uuid,
      );

      if (!planToDelete) return;

      if (planToDelete.isNew) {
        onPlanDelete(row.uuid);
        return;
      }

      try {
        await OfferAPI.deleteOfferPlan({ planId: row.uuid, projectSlug });
        onPlanDelete(row.uuid);
      } catch {
        toast.error(t("error:genericError"));
      }
    },
    [plans, onPlanDelete, projectSlug, t],
  );

  const handleCopyLink = useCallback(
    async (link: string): Promise<void> => {
      await copyToClipboard({ value: link });
    },
    [copyToClipboard],
  );

  const rowList = useMemo(
    () =>
      plans.map((currentPlan) =>
        offerPlanRow({
          plan: currentPlan,
          countryLabels: getSelectedCountries(
            countries,
            currentPlan.countries ?? [],
          ).map(({ label }) => label),
          t,
        }),
      ),
    [plans, countries, t],
  );

  const columnList = useMemo(
    () =>
      offerPlanColumns({
        t,
        planType,
        providerPlans: availableProviderPlanList,
        onEdit: (row) => handleEditClick(row.uuid),
        onCopyLink: handleCopyLink,
        onDelete: handleDelete,
        baseUrl: project?.base_url,
      }),
    [
      t,
      planType,
      availableProviderPlanList,
      handleEditClick,
      handleCopyLink,
      handleDelete,
      project?.base_url,
    ],
  );

  return (
    <React.Fragment>
      <DataTable
        columns={columnList}
        data={rowList}
        emptyText={t("noPlans")}
        onRowClick={(row) => handleEditClick(row.uuid)}
        storageKey={planType === "free" ? undefined : `offerPlans-${planType}`}
        title={t(`pricing_${planType}`)}
        urlSync={{ prefix: planType }}
        headerActions={
          <Button onClick={handleAddPlanClick} size="lg" type="button">
            <Plus />
            {t("add")}
          </Button>
        }
      />
      {editingPlan ? (
        <OfferPlanDrawer
          availablePlatform={availablePlatform}
          availableProviderPlanList={availableProviderPlanList}
          availableRetailerList={retailerList}
          offerId={offerId ?? ""}
          onClose={() => setIsDrawerOpen(false)}
          onSaved={handleDrawerSaved}
          open={isDrawerOpen}
          plan={editingPlan}
          planType={planType}
          projectSlug={projectSlug ?? ""}
        />
      ) : null}
    </React.Fragment>
  );
}

export default OfferPlanTable;
